import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CreateCustomerInput,
  Customer,
  CustomerPage,
  CustomerSegmentationSummary,
  CreateCustomerTagInput,
  CustomerTagDefinition,
  CustomerTagList,
  CustomerTimeline,
  MergeResult,
  UpdateCustomerTagInput,
  UpdateCustomerInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { billingKeys } from '../billing/use-billing.js';
import {
  createCustomer,
  createCustomerTag,
  deleteCustomer,
  deleteCustomerTag,
  getCustomer,
  getCustomerSegmentation,
  getCustomerTimeline,
  listCustomerTags,
  mergeCustomers,
  searchCustomers,
  updateCustomer,
  updateCustomerTag,
  type CustomerSearchParams,
} from './customers-api.js';

/**
 * The customer book's data layer.
 *
 * One key factory, so every mutation invalidates exactly the queries its change could have
 * altered — a renamed customer must disappear from the search that no longer matches them,
 * and a new tag must appear in the filter chips without a reload.
 */
export const customerKeys = {
  all: ['customers'] as const,
  search: (params: CustomerSearchParams) => ['customers', 'search', params] as const,
  detail: (customerId: string) => ['customers', 'detail', customerId] as const,
  // Under `customers` so an edit or a merge — both of which put a row on it — refreshes it too.
  timeline: (customerId: string) => ['customers', 'timeline', customerId] as const,
  tags: () => ['customers', 'tags'] as const,
  segmentation: () => ['customers', 'segmentation'] as const,
};

/**
 * The headline counts above the search box.
 *
 * Keyed without the search parameters on purpose — the figures are shop-wide, so typing in the
 * search box must not refetch them. They still sit under `customers.all`, so creating, merging or
 * deleting a customer invalidates them along with everything else.
 */
export function useCustomerSegmentation(
  options: { enabled?: boolean } = {},
): UseQueryResult<CustomerSegmentationSummary> {
  const client = useApiClient();
  return useQuery({
    queryKey: customerKeys.segmentation(),
    queryFn: () => getCustomerSegmentation(client),
    enabled: options.enabled ?? true,
  });
}

export function useCustomerSearch(
  params: CustomerSearchParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<CustomerPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: customerKeys.search(params),
    queryFn: () => searchCustomers(client, params),
    enabled: options.enabled ?? true,
    // The counter types a digit at a time; showing the previous matches while the next page
    // loads is the difference between a list that updates and a list that flickers.
    placeholderData: (previous) => previous,
  });
}

export function useCustomer(customerId: string | null): UseQueryResult<Customer> {
  const client = useApiClient();
  return useQuery({
    queryKey: customerKeys.detail(customerId ?? ''),
    queryFn: () => getCustomer(client, customerId as string),
    enabled: customerId !== null,
  });
}

export function useCustomerTimeline(customerId: string | null): UseQueryResult<CustomerTimeline> {
  const client = useApiClient();
  return useQuery({
    queryKey: customerKeys.timeline(customerId ?? ''),
    queryFn: () => getCustomerTimeline(client, customerId as string),
    enabled: customerId !== null,
  });
}

export function useCustomerTags(): UseQueryResult<CustomerTagList> {
  const client = useApiClient();
  return useQuery({ queryKey: customerKeys.tags(), queryFn: () => listCustomerTags(client) });
}

/*
 * The managed tag list's three writes. Each invalidates the whole customer tree rather than just
 * the tag list: a rename or delete rewrites customers, and the filter, the rows and the counts
 * all have to read the new names without a reload.
 */

export function useCreateCustomerTag() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CustomerTagDefinition, unknown, CreateCustomerTagInput>({
    mutationFn: (input) => createCustomerTag(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useUpdateCustomerTag() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<
    CustomerTagDefinition,
    unknown,
    { tagId: string; patch: UpdateCustomerTagInput }
  >({
    mutationFn: ({ tagId, patch }) => updateCustomerTag(client, tagId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useDeleteCustomerTag() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (tagId) => deleteCustomerTag(client, tagId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useCreateCustomer() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Customer, unknown, CreateCustomerInput>({
    mutationFn: (input) => createCustomer(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
      // The allowance moved. Without this the in-module meter would keep reporting the figure
      // from before the thing that was just created, which is the one moment it has to be right.
      void queryClient.invalidateQueries({ queryKey: billingKeys.usage() });
    },
  });
}

export function useUpdateCustomer(customerId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Customer, unknown, UpdateCustomerInput>({
    mutationFn: (patch) => updateCustomer(client, customerId, patch),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKeys.detail(customerId), customer);
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useDeleteCustomer() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (customerId) => deleteCustomer(client, customerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useMergeCustomers() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MergeResult, unknown, { survivorId: string; duplicateId: string }>({
    mutationFn: (input) => mergeCustomers(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}
